"use client"

import {useTable, type ColumnDef, type RowData} from "@tanstack/react-table"
import {cn} from "cn"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import {dataTableFeatures, type DataTableFeatures} from "./data-table-features"

export type DataTableProps<TData extends RowData> = {
	columns: ColumnDef<DataTableFeatures, TData>[]
	data: TData[]
	className?: string
	emptyMessage?: string
	headerClassName?: (columnId: string) => string | undefined
	cellClassName?: (columnId: string) => string | undefined
}

export function DataTable<TData extends RowData>({
	columns,
	data,
	className,
	emptyMessage = "Нет данных",
	headerClassName,
	cellClassName,
}: DataTableProps<TData>) {
	const table = useTable({
		features: dataTableFeatures,
		columns,
		data,
	})

	return (
		<div className={cn("overflow-hidden rounded-4xl border border-border", className)}>
			<Table>
				<TableHeader>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRow key={headerGroup.id} className="bg-muted/40 hover:bg-muted/40">
							{headerGroup.headers.map((header) => (
								<TableHead
									key={header.id}
									className={headerClassName?.(header.column.id)}
								>
									{header.isPlaceholder ? null : (
										<table.FlexRender header={header} />
									)}
								</TableHead>
							))}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows.length > 0 ? (
						table.getRowModel().rows.map((row) => (
							<TableRow key={row.id}>
								{row.getAllCells().map((cell) => (
									<TableCell
										key={cell.id}
										className={cellClassName?.(cell.column.id)}
									>
										<table.FlexRender cell={cell} />
									</TableCell>
								))}
							</TableRow>
						))
					) : (
						<TableRow>
							<TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
								{emptyMessage}
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</div>
	)
}
